import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertCircle, ArrowRight, FileText, Lock, Sparkles } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ProgressBar } from '../../components/ui/ProgressBar';
import { Badge } from '../../components/ui/Badge';
import { Skeleton } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { Dropzone } from '../../components/ui/Dropzone';
import { FileStatusBadge } from '../../components/features/FileStatusBadge';
import { UploadProgressModal } from '../../components/features/UploadProgressModal';
import { useCurrentTariff } from '../../hooks/useCurrentTariff';
import { useUploadFlow } from '../../hooks/useUploadFlow';
import { useAuthStore } from '../../store/auth.store';
import { getHistory } from '../../api/files';
import { percent } from '../../lib/format';

export function DashboardPage() {
  const userGUID = useAuthStore((s) => s.user?.guid);
  const tariffQuery = useCurrentTariff();
  const upload = useUploadFlow();

  const recentQuery = useQuery({
    queryKey: ['history', userGUID, 'recent'],
    queryFn: () => {
      if (!userGUID) throw new Error('No user');
      return getHistory(userGUID, { page: 1, limit: 5 });
    },
    enabled: Boolean(userGUID),
  });

  const tariff = tariffQuery.data;
  const used = tariff?.countPagesNow ?? 0;
  const limit = tariff?.tariffLimitPages ?? 0;
  const usagePercent = percent(used, limit);
  const inactive = tariff ? !tariff.tariffActive : false;
  const outOfPages = tariff ? tariff.deltaPagesAvailable <= 0 : false;
  // Загружать можно только если тариф активен и есть свободные страницы.
  const canUpload = Boolean(tariff) && !inactive && !outOfPages;
  // Текст и CTA для «блока вместо дропзоны», если загружать сейчас нельзя.
  const uploadBlocker = !tariff
    ? null
    : inactive
      ? {
          title: 'Тариф завершён',
          description:
            'Загрузка временно недоступна. Продлите тариф или выберите новый — и сразу сможете распознавать.',
          cta: 'Продлить тариф',
        }
      : outOfPages
        ? {
            title: 'Страницы закончились',
            description: `В тарифе «${tariff.tariff}» использованы все ${limit} страниц. Оплатите новый пакет, чтобы продолжить.`,
            cta: 'Пополнить',
          }
        : null;

  return (
    <div className="flex flex-col gap-6">
      <Card className="flex flex-col gap-4">
        {tariffQuery.isLoading ? (
          <>
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-1/2" />
          </>
        ) : tariff ? (
          <>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-semibold text-text">{tariff.tariff}</h2>
                  {inactive && <Badge variant="error">Завершён</Badge>}
                </div>
                <p className="mt-1 text-sm text-text-muted">
                  {inactive
                    ? 'Срок действия тарифа истёк'
                    : `Активен до ${tariff.dateEnd}`}
                </p>
              </div>
              <Link to="/tariffs">
                <Button size="md" rightIcon={<ArrowRight size={16} />}>
                  {inactive ? 'Продлить тариф' : 'Сменить тариф'}
                </Button>
              </Link>
            </div>

            <ProgressBar
              value={usagePercent}
              label={`Использовано ${used} из ${limit} страниц`}
              tone={inactive ? 'muted' : 'brand'}
            />

            {tariff.deltaPagesAvailable <= 0 && (
              <div className="rounded-[14px] bg-red-50 px-3.5 py-2 text-sm text-error">
                Осталось страниц: 0. Пополните тариф, чтобы продолжить.
              </div>
            )}
          </>
        ) : (
          <EmptyState
            icon={<Sparkles size={22} />}
            title="Тариф не определён"
            description="Откройте раздел тарифов и выберите подходящий план."
            action={
              <Link to="/tariffs">
                <Button>К тарифам</Button>
              </Link>
            }
          />
        )}
      </Card>

      {(tariffQuery.isLoading || tariff) && (
        <section className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">Быстрая загрузка</h2>
          {tariffQuery.isLoading ? (
            <Skeleton className="h-32 w-full rounded-[20px]" />
          ) : canUpload ? (
            <Dropzone
              accept="image/jpeg,image/png,application/pdf"
              onFiles={(files) => files[0] && upload.upload(files[0])}
              title="Перетащите JPG, PNG или PDF"
              description={`Осталось ${tariff?.deltaPagesAvailable ?? 0} стр. в тарифе «${tariff?.tariff ?? ''}»`}
              disabled={upload.isUploading}
            />
          ) : (
            uploadBlocker && (
              <Card className="flex flex-col items-start gap-3 border-dashed bg-bg-soft/50">
                <div className="flex items-center gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-brand-primary">
                    {inactive ? <Lock size={18} /> : <AlertCircle size={18} />}
                  </div>
                  <h3 className="text-base font-semibold">{uploadBlocker.title}</h3>
                </div>
                <p className="text-sm text-text-muted">{uploadBlocker.description}</p>
                <Link to="/tariffs">
                  <Button rightIcon={<ArrowRight size={16} />}>
                    {uploadBlocker.cta}
                  </Button>
                </Link>
              </Card>
            )
          )}
        </section>
      )}

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Последние файлы</h2>
          <Link
            to="/files"
            className="text-sm font-medium text-brand-primary hover:underline"
          >
            Показать все
          </Link>
        </div>

        {recentQuery.isLoading ? (
          <div className="flex flex-col gap-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-16 w-full rounded-[16px]" />
            ))}
          </div>
        ) : recentQuery.data && recentQuery.data.files.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {recentQuery.data.files.map((f, i) => (
              <li
                key={f.guid}
                className="animate-enter"
                style={{ animationDelay: `${Math.min(i, 5) * 40}ms` }}
              >
                <Link
                  to={`/files/${f.guid}`}
                  className="press-down flex items-center justify-between gap-3 rounded-[16px] border border-stroke bg-white px-4 py-3 transition-all duration-200 ease-[var(--ease-out-soft)] hover:-translate-y-0.5 hover:border-brand-primary hover:shadow-soft"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-bg-soft text-brand-primary">
                      <FileText size={16} />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{f.alias}</p>
                      <p className="text-xs text-text-muted">
                        {f.date_added} · {f.pages_select} стр.
                      </p>
                    </div>
                  </div>
                  <FileStatusBadge status={f.status} />
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            title="История пока пуста"
            description="Загрузите первый файл — и он появится здесь. Совёнок терпеливо ждёт."
          />
        )}
      </section>

      <UploadProgressModal
        open={upload.isUploading}
        filename={upload.activeFile?.name}
        progress={upload.progress}
      />
    </div>
  );
}
